/**
 * Thin wrapper over `window.Telegram.WebApp`.
 *
 * Everything the app needs from Telegram goes through here, so the rest of the
 * code never has to check whether the SDK is present. Opened in an ordinary
 * browser — which is how most of the development happens — every call becomes a
 * no-op and the app still runs.
 */

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  section_bg_color?: string;
  destructive_text_color?: string;
}

type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type HapticNotification = 'error' | 'success' | 'warning';

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { user?: { id: number; first_name: string }; start_param?: string };
  version: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  viewportStableHeight: number;
  isExpanded: boolean;
  ready(): void;
  expand(): void;
  close(): void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  HapticFeedback?: {
    impactOccurred(style: HapticStyle): void;
    notificationOccurred(type: HapticNotification): void;
    selectionChanged(): void;
  };
  BackButton?: {
    show(): void;
    hide(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
  disableVerticalSwipes?(): void;
  setHeaderColor?(color: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

const webApp = window.Telegram?.WebApp;

export const isTelegram = Boolean(webApp?.initData);

export function initTelegram(): void {
  if (!webApp) return;
  webApp.ready();
  webApp.expand();
  // Swiping down inside a board would otherwise close the Mini App mid-move.
  webApp.disableVerticalSwipes?.();
  applyTheme();
  webApp.onEvent('themeChanged', applyTheme);
}

/**
 * Copies Telegram's palette onto CSS variables. The design brief was to look
 * native inside Telegram, so the app takes its colours from the client rather
 * than shipping its own.
 */
function applyTheme(): void {
  if (!webApp) return;
  const root = document.documentElement;
  const params = webApp.themeParams;

  const map: Record<string, string | undefined> = {
    '--tg-bg': params.bg_color,
    '--tg-text': params.text_color,
    '--tg-hint': params.hint_color,
    '--tg-link': params.link_color,
    '--tg-button': params.button_color,
    '--tg-button-text': params.button_text_color,
    '--tg-secondary-bg': params.secondary_bg_color,
    '--tg-section-bg': params.section_bg_color,
    '--tg-destructive': params.destructive_text_color,
  };

  for (const [name, value] of Object.entries(map)) {
    if (value) root.style.setProperty(name, value);
  }

  root.dataset.theme = webApp.colorScheme;
  webApp.setHeaderColor?.(params.secondary_bg_color ?? params.bg_color ?? '#ffffff');
}

export function getInitData(): string {
  return webApp?.initData ?? '';
}

/** `?startapp=` payload, used to jump straight into a match from a link. */
export function getStartParam(): string | null {
  return webApp?.initDataUnsafe?.start_param ?? null;
}

export function closeApp(): void {
  webApp?.close();
}

// ---------------------------------------------------------------------------
// back button
// ---------------------------------------------------------------------------

export function showBackButton(handler: () => void): () => void {
  const button = webApp?.BackButton;
  if (!button) return () => undefined;
  button.onClick(handler);
  button.show();
  return () => {
    button.offClick(handler);
    button.hide();
  };
}

// ---------------------------------------------------------------------------
// haptics
// ---------------------------------------------------------------------------

let hapticsEnabled = true;

export function setHapticsEnabled(enabled: boolean): void {
  hapticsEnabled = enabled;
}

export function haptic(style: HapticStyle | HapticNotification | 'selection'): void {
  if (!hapticsEnabled) return;
  const feedback = webApp?.HapticFeedback;
  if (!feedback) return;

  try {
    if (style === 'selection') feedback.selectionChanged();
    else if (style === 'success' || style === 'error' || style === 'warning') {
      feedback.notificationOccurred(style);
    } else feedback.impactOccurred(style);
  } catch {
    // Older clients throw on unsupported styles; a missing buzz is not a bug
    // worth surfacing.
  }
}
